import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useState } from "react";
import { Loader2 } from "lucide-react";
import { SmsHubLayout } from "@/components/sms/SmsHubLayout";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { listBatches, listBatchContacts } from "@/server/sms.functions";
import { useAuth } from "@/hooks/useAuth";

export const Route = createFileRoute("/dashboard/admin/sms/matching")({
  head: () => ({ meta: [{ title: "Matching · SMS" }, { name: "robots", content: "noindex" }] }),
  validateSearch: (s: Record<string, unknown>) => ({ batch: typeof s.batch === "string" ? s.batch : undefined }),
  component: Page,
});

const STATUS_COLORS: Record<string, string> = {
  matched: "bg-emerald-500/15 text-emerald-700",
  possible: "bg-blue-500/15 text-blue-700",
  not_matched: "bg-muted text-foreground",
  duplicate: "bg-purple-500/15 text-purple-700",
  invalid_phone: "bg-rose-500/15 text-rose-700",
  not_sms_eligible: "bg-rose-500/15 text-rose-700",
  already_contacted: "bg-amber-500/15 text-amber-700",
  opted_out: "bg-rose-500/15 text-rose-700",
  suppressed: "bg-rose-500/15 text-rose-700",
};

function Page() {
  const { primaryRole } = useAuth();
  const search = useSearch({ from: "/dashboard/admin/sms/matching" });
  const [batchId, setBatchId] = useState<string | undefined>(search.batch);
  const [status, setStatus] = useState<string>("");

  const listB = useServerFn(listBatches);
  const listC = useServerFn(listBatchContacts);

  const { data: batches } = useQuery({ queryKey: ["sms-batches"], queryFn: () => listB(), enabled: primaryRole === "admin" });
  const { data: contacts, isLoading } = useQuery({
    queryKey: ["sms-batch-contacts", batchId, status],
    queryFn: () => listC({ data: { batchId: batchId!, status: status || undefined } }),
    enabled: !!batchId,
  });

  return (
    <SmsHubLayout title="Provider Matching">
      <div className="flex gap-3 mb-3">
        <select
          value={batchId ?? ""}
          onChange={(e) => setBatchId(e.target.value || undefined)}
          className="h-9 border border-border rounded-md px-2 text-[13px] bg-background"
        >
          <option value="">Select a batch…</option>
          {(batches ?? []).map((b: any) => (
            <option key={b.id} value={b.id}>{b.filename} ({b.row_count})</option>
          ))}
        </select>
        <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 border border-border rounded-md px-2 text-[13px] bg-background">
          <option value="">All statuses</option>
          {Object.keys(STATUS_COLORS).map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
      </div>

      {!batchId && <p className="text-[13px] text-muted-foreground">Pick a batch to review matches.</p>}
      {batchId && (
        <div className="card-surface border border-border rounded-md">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Practice / Provider</TableHead>
                <TableHead>Phone</TableHead>
                <TableHead>NPI</TableHead>
                <TableHead>City/State</TableHead>
                <TableHead>Match status</TableHead>
                <TableHead>Method</TableHead>
                <TableHead>Score</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading && (
                <TableRow><TableCell colSpan={7} className="text-center py-6"><Loader2 className="h-4 w-4 animate-spin inline" /></TableCell></TableRow>
              )}
              {!isLoading && (contacts ?? []).map((c: any) => (
                <TableRow key={c.id}>
                  <TableCell className="text-[12.5px]">
                    <div className="font-medium">{c.practice_name ?? c.provider_name ?? "—"}</div>
                    {c.provider_name && c.practice_name && <div className="text-muted-foreground text-[11px]">{c.provider_name}</div>}
                  </TableCell>
                  <TableCell className="font-mono text-[12px]">{c.phone_e164 ?? c.phone_raw}</TableCell>
                  <TableCell className="font-mono text-[12px]">{c.npi ?? "—"}</TableCell>
                  <TableCell className="text-[12px]">{[c.city, c.state].filter(Boolean).join(", ") || "—"}</TableCell>
                  <TableCell>
                    <span className={`text-[10.5px] px-2 py-0.5 rounded-full ${STATUS_COLORS[c.match_status] ?? "bg-muted"}`}>{c.match_status}</span>
                  </TableCell>
                  <TableCell className="text-[12px] text-muted-foreground">{c.match_method ?? "—"}</TableCell>
                  <TableCell className="text-[12px] tabular-nums">{c.match_score?.toFixed?.(2) ?? "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </SmsHubLayout>
  );
}
